(() => {
  class ExactProductSearchPagination {
    constructor() {
      this.runId = 0;
      this.timer = null;
      this.htmlCache = new Map();
      this.onFacetsChange = this.onFacetsChange.bind(this);

      document.addEventListener("facets:change", this.onFacetsChange);
      this.refresh();
    }

    onFacetsChange() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.htmlCache.clear();
        this.refresh();
      }, 250);
    }

    getSearchTab() {
      return document.querySelector("search-tab[data-section-id]");
    }

    getSectionId() {
      return this.getSearchTab()?.dataset.sectionId || "";
    }

    getDisplayTerm() {
      const tabTerm = this.getSearchTab()?.dataset.terms || "";
      const url = new URL(window.location.href);
      const raw = (url.searchParams.get("q") || tabTerm).trim();
      if (!raw) return "";

      const titleParts = raw.match(/title:"[^"]+"|title:[^\s]+/g);
      const isLegacyTitleQuery =
        titleParts?.length &&
        raw.replace(/title:"[^"]+"|title:[^\s]+/g, "").trim() === "";

      if (isLegacyTitleQuery) {
        return titleParts
          .map((part) =>
            part
              .replace(/^title:/, "")
              .replace(/^"|"$/g, "")
              .replace(/\*$/, ""),
          )
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
      }

      return raw.replace(/["\\]/g, " ").replace(/\s+/g, " ").trim();
    }

    normalizeQuery(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/["'\\*]/g, " ")
        .replace(/[-\/,.()&:|_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    normalizeTitle(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[-\/,.()&:|_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    normalizeSku(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .trim();
    }

    getWords() {
      const normalized = this.normalizeQuery(this.getDisplayTerm());
      return normalized ? normalized.split(" ").filter(Boolean) : [];
    }

    getCandidateWords() {
      const stopWords = new Set([
        "a",
        "an",
        "and",
        "at",
        "by",
        "for",
        "from",
        "in",
        "of",
        "on",
        "or",
        "the",
        "to",
        "with",
      ]);
      const words = this.getWords();
      const meaningful = words.filter((word) => !stopWords.has(word));
      return meaningful.length ? meaningful : words;
    }

    shouldForcePrefix() {
      return /\d/.test(this.normalizeQuery(this.getDisplayTerm()));
    }

    buildTitleCandidateQuery() {
      const words = this.getCandidateWords();
      if (!words.length) return "";
      return words
        .map((word, index) =>
          index === words.length - 1 ? `title:${word}*` : `title:${word}`,
        )
        .join(" ");
    }

    looksLikeSkuQuery() {
      return /^sk[-\s]/i.test(this.getDisplayTerm().trim());
    }

    buildSectionUrl(page, mode) {
      const url = new URL(window.location.href);
      const query =
        mode === "raw"
          ? this.getDisplayTerm()
          : this.buildTitleCandidateQuery();

      url.searchParams.set("q", query || this.getDisplayTerm());
      url.searchParams.set("type", "product");
      url.searchParams.set(
        "options[prefix]",
        mode === "exact" ? "none" : "last",
      );
      url.searchParams.set("options[unavailable_products]", "last");
      url.searchParams.set("section_id", this.getSectionId());

      if (page > 1) url.searchParams.set("page", String(page));
      else url.searchParams.delete("page");

      return url.toString();
    }

    async fetchGrid(page, mode) {
      const url = this.buildSectionUrl(page, mode);
      let request = this.htmlCache.get(url);

      if (!request) {
        request = fetch(url, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          credentials: "same-origin",
        }).then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        });

        this.htmlCache.set(url, request);
        if (this.htmlCache.size > 30) this.htmlCache.clear();
      }

      let html;
      try {
        html = await request;
      } catch (error) {
        this.htmlCache.delete(url);
        throw error;
      }

      const parsed = new DOMParser().parseFromString(html, "text/html");
      const grid = parsed.querySelector("#product-grid");
      if (!grid) {
        throw new Error("Product search grid missing from section response");
      }

      return grid;
    }

    resultCount(grid) {
      const text = grid?.querySelector(".collection__counter p")?.textContent || "";
      const match = text.match(/\d[\d,]*/);
      return match ? Number.parseInt(match[0].replace(/,/g, ""), 10) || 0 : 0;
    }

    pageCount(grid) {
      let maxPage = 1;
      const pagination = grid?.querySelector(".wt-pagination-wrapper");
      if (!pagination) return maxPage;

      pagination.querySelectorAll(".wt-pagination__item").forEach((item) => {
        const visiblePage = Number.parseInt(item.textContent.trim(), 10);
        if (Number.isFinite(visiblePage)) maxPage = Math.max(maxPage, visiblePage);

        const href = item.getAttribute("href");
        if (!href) return;

        try {
          const pageUrl = new URL(href, window.location.origin);
          const hrefPage = Number.parseInt(
            pageUrl.searchParams.get("page") || "1",
            10,
          );
          if (Number.isFinite(hrefPage)) maxPage = Math.max(maxPage, hrefPage);
        } catch (error) {
          // Keep parsing the remaining pagination items.
        }
      });

      return maxPage;
    }

    itemsFromGrid(grid) {
      return Array.from(grid?.children || [])
        .filter((child) => child.classList?.contains("collection__grid__item"))
        .map((node) => {
          const link = node.querySelector(".card__title a");
          const title = link?.textContent?.replace(/\s+/g, " ").trim();
          const href = link?.getAttribute("href");
          if (!title || !href) return null;

          let key = href.split("?")[0].split("#")[0];
          try {
            const productUrl = new URL(href, window.location.origin);
            key = productUrl.pathname;
          } catch (error) {
            // Relative path fallback above is sufficient.
          }

          const skus = String(node.dataset.searchSkus || "")
            .split("|")
            .map((sku) => sku.trim())
            .filter(Boolean);

          return { node, title, key, skus };
        })
        .filter(Boolean);
    }

    makeScopedPlan(mode, firstGrid) {
      const total = this.resultCount(firstGrid);
      if (total <= 0) return { status: "empty", mode, firstGrid, total: 0 };

      const firstItems = this.itemsFromGrid(firstGrid);
      const nativePages = this.pageCount(firstGrid);
      const firstCount = firstItems.length;

      if (!firstCount) {
        return { status: "invalid", mode, firstGrid, total };
      }

      if (nativePages <= 1) {
        if (firstCount !== total) {
          return { status: "invalid", mode, firstGrid, total };
        }

        return {
          status: "ok",
          mode,
          firstGrid,
          firstItems,
          total,
          pageSize: Math.max(firstCount, 1),
        };
      }

      if (Math.ceil(total / firstCount) !== nativePages) {
        return { status: "invalid", mode, firstGrid, total };
      }

      return {
        status: "ok",
        mode,
        firstGrid,
        firstItems,
        total,
        pageSize: firstCount,
      };
    }

    async resolveScopedPlan(runId) {
      if (runId !== this.runId) return null;
      return null;
    }

    matchState(title, skus = []) {
      const searchWords = this.getWords();
      const titleWords = this.normalizeTitle(title).split(" ").filter(Boolean);
      const skuQuery = this.normalizeSku(this.getDisplayTerm());
      const normalizedSkus = skus.map((sku) => this.normalizeSku(sku)).filter(Boolean);

      let titleExact = false;
      let titlePrefix = false;

      if (searchWords.length && titleWords.length) {
        titleExact = searchWords.every((word) => titleWords.includes(word));
        titlePrefix = true;

        searchWords.forEach((word, index) => {
          if (!titlePrefix) return;
          const isLast = index === searchWords.length - 1;
          if (isLast) {
            titlePrefix = titleWords.some((titleWord) => titleWord.startsWith(word));
          } else if (!titleWords.includes(word)) {
            titlePrefix = false;
          }
        });
      }

      const skuExact =
        Boolean(skuQuery) && normalizedSkus.some((sku) => sku === skuQuery);
      const skuPrefix =
        Boolean(skuQuery) && normalizedSkus.some((sku) => sku.startsWith(skuQuery));

      return {
        exact: titleExact || skuExact,
        prefix: titlePrefix || skuPrefix,
      };
    }

    finalMatches(items) {
      const unique = [];
      const seen = new Set();

      items.forEach((item) => {
        if (!item?.key || seen.has(item.key)) return;
        seen.add(item.key);
        unique.push({ ...item, match: this.matchState(item.title, item.skus) });
      });

      return unique.filter((item) => item.match.prefix);
    }

    async fetchAllPages(mode, firstGrid, runId) {
      const totalPages = this.pageCount(firstGrid);
      const grids = new Array(totalPages);
      grids[0] = firstGrid;
      const pages = Array.from(
        { length: Math.max(0, totalPages - 1) },
        (_, index) => index + 2,
      );

      for (let index = 0; index < pages.length; index += 8) {
        if (runId !== this.runId) return [];
        const batch = pages.slice(index, index + 8);
        const batchGrids = await Promise.all(
          batch.map((page) => this.fetchGrid(page, mode)),
        );

        batch.forEach((page, batchIndex) => {
          grids[page - 1] = batchGrids[batchIndex];
        });
      }

      if (runId !== this.runId) return [];
      return grids.flatMap((grid) => this.itemsFromGrid(grid));
    }

    productsPerPage(grid) {
      const total = this.resultCount(grid);
      const pages = this.pageCount(grid);
      const candidates = [];

      for (let size = 12; size <= 120; size += 12) {
        if (Math.ceil(total / size) === pages) candidates.push(size);
      }

      if (candidates.includes(24)) return 24;
      return candidates[0] || 24;
    }

    async loadLegacyMatches(runId, firstGrid = null) {
      const rawFirst = firstGrid || (await this.fetchGrid(1, "raw"));
      if (runId !== this.runId) return null;

      const pageSize = this.productsPerPage(rawFirst);
      const rawItems = await this.fetchAllPages("raw", rawFirst, runId);
      if (runId !== this.runId) return null;

      return {
        matches: this.finalMatches(rawItems),
        pageSize,
      };
    }

    currentPage() {
      const url = new URL(window.location.href);
      const page = Number.parseInt(url.searchParams.get("page") || "1", 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    paginationUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.delete("section_id");
      if (page <= 1) url.searchParams.delete("page");
      else url.searchParams.set("page", String(page));
      return `${url.pathname}${url.search}${url.hash}`;
    }

    paginationParts(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }

      const pages = new Set([1, total]);
      for (
        let page = Math.max(2, current - 2);
        page <= Math.min(total - 1, current + 2);
        page += 1
      ) {
        pages.add(page);
      }

      const sorted = Array.from(pages).sort((a, b) => a - b);
      const parts = [];
      sorted.forEach((page, index) => {
        if (index && page - sorted[index - 1] > 1) parts.push("ellipsis");
        parts.push(page);
      });
      return parts;
    }

    makePagination(current, total) {
      if (total <= 1) return null;

      const wrapper = document.createElement("div");
      wrapper.className = "wt-pagination-wrapper";
      const nav = document.createElement("nav");
      nav.className = "wt-pagination";
      nav.setAttribute("role", "navigation");
      nav.setAttribute("aria-label", "Pagination");
      const list = document.createElement("ul");
      list.className = "wt-pagination__list list-unstyled";
      list.setAttribute("role", "list");

      const addLink = (label, page, className, ariaLabel) => {
        const li = document.createElement("li");
        const a = document.createElement("a");
        a.href = this.paginationUrl(page);
        a.className = className;
        a.setAttribute("aria-label", ariaLabel);
        a.textContent = label;
        li.appendChild(a);
        list.appendChild(li);
      };

      if (current > 1) {
        addLink(
          "‹",
          current - 1,
          "wt-pagination__item wt-pagination__item--next wt-pagination__item-arrow link motion-reduce",
          "Previous page",
        );
      }

      this.paginationParts(current, total).forEach((part) => {
        const li = document.createElement("li");

        if (part === "ellipsis") {
          const span = document.createElement("span");
          span.className = "wt-pagination__item";
          span.textContent = "…";
          li.appendChild(span);
        } else if (part === current) {
          const span = document.createElement("span");
          span.className = "wt-pagination__item wt-pagination__item--current";
          span.setAttribute("aria-current", "page");
          span.setAttribute("aria-label", `Page ${part}`);
          span.textContent = String(part);
          li.appendChild(span);
        } else {
          const a = document.createElement("a");
          a.href = this.paginationUrl(part);
          a.className = "wt-pagination__item link";
          a.setAttribute("aria-label", `Page ${part}`);
          a.textContent = String(part);
          li.appendChild(a);
        }

        list.appendChild(li);
      });

      if (current < total) {
        addLink(
          "›",
          current + 1,
          "wt-pagination__item wt-pagination__item--prev wt-pagination__item-arrow link motion-reduce",
          "Next page",
        );
      }

      nav.appendChild(list);
      wrapper.appendChild(nav);
      return wrapper;
    }

    prepareCurrentGrid(grid) {
      grid.setAttribute("aria-busy", "true");

      Array.from(grid.children).forEach((child) => {
        if (
          child.classList?.contains("search-results__no-results") ||
          child.classList?.contains("wt-pagination-wrapper")
        ) {
          child.style.display = "none";
        }
      });
    }

    revealAccurateCount(grid, total) {
      if (!grid) return;
      this.updateCounter(grid, total);
      const counter = grid.querySelector(".collection__counter p");
      if (counter) counter.style.visibility = "visible";
    }

    updateCounter(grid, total) {
      const counter = grid.querySelector(".collection__counter p");
      if (!counter) return;

      const formatted = new Intl.NumberFormat().format(total);
      if (/\d[\d,]*/.test(counter.textContent)) {
        counter.textContent = counter.textContent.replace(/\d[\d,]*/, formatted);
      } else {
        counter.textContent = `${formatted} results`;
      }

      if (total === 1) {
        counter.textContent = counter.textContent.replace(/\bresults\b/i, "result");
      } else {
        counter.textContent = counter.textContent.replace(/\bresult\b(?!s)/i, "results");
      }
    }

    updateNoResults(grid, total) {
      let message = grid.querySelector(".search-results__no-results");
      const toolbar = grid.querySelector(".collection__toolbar");

      if (total) {
        message?.remove();
        toolbar?.classList.remove("hidden");
        return;
      }

      toolbar?.classList.add("hidden");
      if (!message) {
        message = document.createElement("p");
        message.className = "search-results__no-results";
        message.textContent = `No products found for “${this.getDisplayTerm()}”. Check the spelling or use a different word or phrase.`;
        grid.appendChild(message);
      } else {
        message.style.display = "";
      }
    }

    clearRenderedResults(grid) {
      Array.from(grid.children).forEach((child) => {
        if (
          child.classList?.contains("collection__grid__item") ||
          child.matches?.("template[data-exact-search-suggestion]") ||
          child.matches?.("[data-exact-search-result-count]") ||
          child.classList?.contains("wt-pagination-wrapper") ||
          child.classList?.contains("search-results__no-results")
        ) {
          child.remove();
        }
      });
    }

    renderPage(items, total, pageSize, currentPage) {
      const grid = document.querySelector("#product-grid");
      if (!grid) return;

      const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
      const requestedPage = currentPage || this.currentPage();
      const safePage = Math.min(Math.max(requestedPage, 1), totalPages);

      if (requestedPage !== safePage) {
        const corrected = new URL(window.location.href);
        if (safePage <= 1) corrected.searchParams.delete("page");
        else corrected.searchParams.set("page", String(safePage));
        window.history.replaceState({}, "", corrected.toString());
      }

      const currentKeys = this.itemsFromGrid(grid).map((item) => item.key);
      const nextKeys = items.map((item) => item.key);
      const canReuseProducts =
        currentKeys.length === nextKeys.length &&
        currentKeys.every((key, index) => key === nextKeys[index]);

      if (canReuseProducts) {
        Array.from(grid.children).forEach((child) => {
          if (
            child.matches?.("template[data-exact-search-suggestion]") ||
            child.matches?.("[data-exact-search-result-count]") ||
            child.classList?.contains("wt-pagination-wrapper") ||
            child.classList?.contains("search-results__no-results")
          ) {
            child.remove();
          }
        });
      } else {
        this.clearRenderedResults(grid);

        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item.node.cloneNode(true)));
        grid.appendChild(fragment);
      }

      this.updateCounter(grid, total);
      this.updateNoResults(grid, total);

      const pagination = this.makePagination(safePage, totalPages);
      if (pagination) grid.appendChild(pagination);
    }

    async renderScopedPlan(plan, runId) {
      const requestedPage = this.currentPage();
      const totalPages = Math.max(1, Math.ceil(plan.total / plan.pageSize));
      const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);

      let pageGrid = plan.firstGrid;
      let pageItems = plan.firstItems;

      if (currentPage > 1) {
        pageGrid = await this.fetchGrid(currentPage, plan.mode);
        if (runId !== this.runId) return false;
        pageItems = this.itemsFromGrid(pageGrid);
      }

      const expected = Math.max(
        0,
        Math.min(
          plan.pageSize,
          plan.total - (currentPage - 1) * plan.pageSize,
        ),
      );

      if (pageItems.length !== expected) return false;

      this.renderPage(pageItems, plan.total, plan.pageSize, currentPage);
      return true;
    }

    async renderLegacy(runId, firstGrid = null) {
      const legacy = await this.loadLegacyMatches(runId, firstGrid);
      if (runId !== this.runId || !legacy) return;

      const total = legacy.matches.length;
      const totalPages = Math.max(
        1,
        Math.ceil(total / Math.max(legacy.pageSize, 1)),
      );
      const requestedPage = this.currentPage();
      const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
      const start = (currentPage - 1) * legacy.pageSize;
      const visible = legacy.matches.slice(start, start + legacy.pageSize);

      this.revealAccurateCount(document.querySelector("#product-grid"), total);
      this.renderPage(visible, total, legacy.pageSize, currentPage);
    }

    tryRenderCurrentGrid(grid) {
      if (this.currentPage() !== 1 || this.pageCount(grid) > 1) return false;

      const nativeTotal = this.resultCount(grid);
      const items = this.itemsFromGrid(grid);
      if (nativeTotal <= 0 || items.length !== nativeTotal) return false;

      const matches = this.finalMatches(items);
      this.renderPage(
        matches,
        matches.length,
        Math.max(nativeTotal, 1),
        1,
      );
      return true;
    }

    async refresh() {
      const grid = document.querySelector("#product-grid");
      if (!grid || !this.getSectionId() || !this.getDisplayTerm()) return;

      const runId = ++this.runId;
      this.prepareCurrentGrid(grid);

      try {
        const firstGrid = this.currentPage() === 1 ? grid : null;

        if (firstGrid && this.tryRenderCurrentGrid(firstGrid)) return;

        await this.renderLegacy(runId, firstGrid);
      } catch (error) {
        if (runId === this.runId) {
          console.error("Exact product search pagination failed:", error);
        }
      } finally {
        if (runId === this.runId) {
          const currentGrid = document.querySelector("#product-grid");
          currentGrid?.removeAttribute("aria-busy");
          const counter = currentGrid?.querySelector(".collection__counter p");
          counter?.style.removeProperty("visibility");
        }
      }
    }
  }

  const startExactProductPagination = () => {
    if (!document.querySelector("search-tab[data-section-id]")) return;
    if (window.__exactProductSearchPagination) return;
    window.__exactProductSearchPagination = new ExactProductSearchPagination();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startExactProductPagination, {
      once: true,
    });
  } else {
    startExactProductPagination();
  }
})();
